import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useReducedMotion } from '../hooks/useReducedMotion'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastInput {
  type?: ToastType
  message: string
  title?: string
  /** Auto-dismiss delay in ms. Default 4000. Pass `0` to disable auto-dismiss. */
  duration?: number
  action?: ToastAction
}

export interface ToastRecord extends Required<Omit<ToastInput, 'title' | 'action'>> {
  id: string
  title?: string
  action?: ToastAction
}

export interface ToastApi {
  showToast: (input: ToastInput) => string
  showSuccess: (message: string, opts?: Omit<ToastInput, 'type' | 'message'>) => string
  showError: (message: string, opts?: Omit<ToastInput, 'type' | 'message'>) => string
  showInfo: (message: string, opts?: Omit<ToastInput, 'type' | 'message'>) => string
  showWarning: (message: string, opts?: Omit<ToastInput, 'type' | 'message'>) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const MAX_VISIBLE = 4
const DEFAULT_DURATION = 4000

const TYPE_STYLES: Record<ToastType, string> = {
  success: 'bg-accent-success/95 border-accent-success text-white',
  error: 'bg-accent-danger/95 border-accent-danger text-white',
  info: 'bg-blue-600/95 border-blue-500 text-white',
  warning: 'bg-accent-warning/95 border-accent-warning text-black',
}

const TYPE_ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
  warning: '!',
}

let toastIdCounter = 0
function nextId(): string {
  toastIdCounter += 1
  return `toast_${Date.now()}_${toastIdCounter}`
}

export interface ToastProviderProps {
  children: ReactNode
  /** Maximum number of toasts visible at once. Default 4. */
  maxVisible?: number
}

/**
 * Provides the toast notification context. Renders a portaled stack at
 * top-right on wide viewports and bottom-center on narrow (<640px)
 * viewports. Auto-dismisses after `duration` ms and supports a single
 * optional action button per toast.
 */
export function ToastProvider({
  children,
  maxVisible = MAX_VISIBLE,
}: ToastProviderProps) {
  const [queue, setQueue] = useState<ToastRecord[]>([])
  const timeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const reduceMotion = useReducedMotion()

  const dismiss = useCallback((id: string) => {
    const timeout = timeoutsRef.current.get(id)
    if (timeout) {
      clearTimeout(timeout)
      timeoutsRef.current.delete(id)
    }
    setQueue((current) => current.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback(
    (input: ToastInput): string => {
      const id = nextId()
      const record: ToastRecord = {
        id,
        type: input.type ?? 'info',
        message: input.message,
        duration: input.duration ?? DEFAULT_DURATION,
        title: input.title,
        action: input.action,
      }

      setQueue((current) => [...current, record])

      if (record.duration > 0) {
        const timeout = setTimeout(() => {
          dismiss(id)
        }, record.duration)
        timeoutsRef.current.set(id, timeout)
      }

      return id
    },
    [dismiss]
  )

  const showSuccess = useCallback<ToastApi['showSuccess']>(
    (message, opts) => showToast({ ...opts, type: 'success', message }),
    [showToast]
  )
  const showError = useCallback<ToastApi['showError']>(
    (message, opts) => showToast({ ...opts, type: 'error', message }),
    [showToast]
  )
  const showInfo = useCallback<ToastApi['showInfo']>(
    (message, opts) => showToast({ ...opts, type: 'info', message }),
    [showToast]
  )
  const showWarning = useCallback<ToastApi['showWarning']>(
    (message, opts) => showToast({ ...opts, type: 'warning', message }),
    [showToast]
  )

  // Clear all pending timers on unmount.
  useEffect(() => {
    const timeouts = timeoutsRef.current
    return () => {
      timeouts.forEach((t) => clearTimeout(t))
      timeouts.clear()
    }
  }, [])

  const api = useMemo<ToastApi>(
    () => ({ showToast, showSuccess, showError, showInfo, showWarning, dismiss }),
    [showToast, showSuccess, showError, showInfo, showWarning, dismiss]
  )

  const visible = queue.slice(0, maxVisible)

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            // Top-right on desktop, bottom on small viewports. `pointer-events-none`
            // so the rest of the UI remains interactive; children re-enable it.
            className="pointer-events-none fixed inset-x-0 bottom-4 z-[11000] flex flex-col items-center gap-2 px-4 sm:inset-auto sm:bottom-auto sm:right-4 sm:top-4 sm:items-end"
            aria-live="polite"
            aria-atomic="false"
          >
            {visible.map((toast) => (
              <ToastItem
                key={toast.id}
                toast={toast}
                onDismiss={() => dismiss(toast.id)}
                reduceMotion={reduceMotion}
              />
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  )
}

interface ToastItemProps {
  toast: ToastRecord
  onDismiss: () => void
  reduceMotion: boolean
}

function ToastItem({ toast, onDismiss, reduceMotion }: ToastItemProps) {
  const { type, title, message, action } = toast
  const role = type === 'error' ? 'alert' : 'status'
  const ariaLive = type === 'error' ? 'assertive' : 'polite'

  const animation = reduceMotion
    ? ''
    : 'animate-fade-in-up sm:animate-fade-in'

  return (
    <div
      role={role}
      aria-live={ariaLive}
      className={`pointer-events-auto w-full max-w-sm rounded-lg border-l-4 shadow-cinematic ${TYPE_STYLES[type]} ${animation}`}
    >
      <div className="flex items-start gap-3 p-3">
        <span
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-bold"
          aria-hidden="true"
        >
          {TYPE_ICONS[type]}
        </span>
        <div className="min-w-0 flex-1">
          {title && (
            <div className="text-sm font-semibold leading-tight">{title}</div>
          )}
          <div className="text-sm leading-snug break-words">{message}</div>
        </div>
        {action && (
          <button
            type="button"
            onClick={() => {
              action.onClick()
              onDismiss()
            }}
            className="flex-shrink-0 rounded bg-white/20 px-2 py-1 text-xs font-semibold uppercase tracking-wide hover:bg-white/30 transition-colors"
          >
            {action.label}
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="flex-shrink-0 rounded p-1 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Dismiss notification"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}

export { ToastContext }
